import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  authClient,
  type AuthUser,
  type AuthSession,
  hasPermission,
  getBackendUrl,
} from '@lib/betterAuth';
import { Analytics, setUserProperties, setUserId } from '@lib/analytics';
import { storeSession, type StoredSession } from '@lib/session';
import { AUTH_UNAUTHORIZED_EVENT } from '@lib/api';
import { queryClient } from '@lib/queryClient';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

interface AuthContextType {
  user: AuthUser | null;
  session: AuthSession | null;
  isAuthenticated: boolean;
  loading: boolean;
  signIn: (credentials: {
    email: string;
    password: string;
  }) => Promise<{ success: boolean; error?: string; emailNotVerified?: boolean; status?: number }>;
  signUp: (userData: {
    email: string;
    password: string;
    name: string;
  }) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  hasRole: (role: AuthUser['role']) => boolean;
  isAdmin: boolean;
  displayName: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** The admin-role predicate, defined ONCE and used by both readers of it —
 *  `contextValue.isAdmin` and the no-throw `useOptionalIsAdmin` below. They had
 *  drifted into two independent spellings of the same comparison, which is one
 *  edit away from the two disagreeing about who counts as an admin. */
const isAdminUser = (user: Pick<AuthUser, 'role'> | null | undefined): boolean =>
  user?.role === 'admin';

/** Auth user id when a provider is mounted, else null. Never throws: the login
 *  page and unit tests render without a provider, so preferences use device scope. */
export const useOptionalAuthUserId = (): string | null => {
  const context = useContext(AuthContext);
  return context?.user?.id ?? null;
};

/** Whether the caller holds the admin role, or `false` with no provider mounted.
 *
 *  The no-throw sibling of `useAuth().isAdmin`, and the reason it exists: the
 *  resource kit's services (#2165) are assembled at the app root and in the
 *  component-test provider tree, and the latter mounts no `BetterAuthProvider`.
 *  Calling `useAuth()` there would throw for ~66 suites that legitimately do not
 *  care who the caller is. "No provider" and "not an admin" mean the same safe
 *  thing here — show no privileged affordance — exactly as for `usePermissions`
 *  below, and this never widens access: route-level enforcement still runs
 *  through `PermissionGuard` / `useBetterAuth`. The role predicate is
 *  `isAdminUser` above — the same function `contextValue.isAdmin` calls, so
 *  "kept in one place" is now a property of the code rather than a claim about
 *  it. */
export const useOptionalIsAdmin = (): boolean => {
  const context = useContext(AuthContext);
  return isAdminUser(context?.user);
};

type SessionPayload = {
  session?: { token: string; expiresAt: string | Date };
  token?: string;
};

const toAuthSession = (payload?: SessionPayload | null): AuthSession | null => {
  if (!payload) {
    return null;
  }

  if (payload.session) {
    return {
      token: payload.session.token,
      expiresAt: new Date(payload.session.expiresAt),
    };
  }

  if (payload.token) {
    return {
      token: payload.token,
      expiresAt: new Date(Date.now() + 1000 * 60 * 15),
    };
  }

  return null;
};

/** Loose Better Auth user payload — every field is untrusted input,
 *  narrowed field-by-field by `toAuthUser`. Its `role` is the PLATFORM role
 *  (`user` / `platform_admin`), so it is deliberately not read here. */
type AuthUserPayload = {
  id?: unknown;
  email?: unknown;
  name?: unknown;
  emailVerified?: unknown;
  image?: unknown;
};

// Tenant-role allowlist. The allowlist + default must stay in sync with
// `lib/betterAuth.ts` `AuthUser['role']` and backend `TENANT_MEMBER_ROLE`.
const VALID_ROLES = ['admin', 'operator', 'viewer'] as const;

/** Narrow an untrusted tenant role. Missing/unknown values fall back to
 *  `viewer` so an unrecognised value fails closed (read-only) rather than
 *  silently promoting. */
const toTenantRole = (value: unknown): AuthUser['role'] =>
  typeof value === 'string' && (VALID_ROLES as readonly string[]).includes(value)
    ? (value as AuthUser['role'])
    : 'viewer';

/** Exported for unit tests — see __tests__/BetterAuthContext.test.tsx. */
export const toAuthUser = (payload?: unknown): AuthUser | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const p = payload as AuthUserPayload;

  return {
    id: String(p.id ?? ''),
    email: String(p.email ?? ''),
    name: String(p.name ?? ''),
    // The tenant role is not in the session payload: start fail-closed at
    // `viewer`; `loadPermissions` applies the role from GET /api/permissions.
    role: 'viewer',
    // permissions left undefined until fetchPermissions completes
    emailVerified: Boolean(p.emailVerified),
    image: typeof p.image === 'string' ? p.image : undefined,
  };
};

const persistSession = (session: AuthSession | null) => {
  if (session) {
    const stored: StoredSession = {
      token: session.token,
      expiresAt: new Date(session.expiresAt).toISOString(),
    };
    storeSession(stored);
  } else {
    storeSession(null);
  }
};

/** Thrown by `fetchPermissionGrants` on a non-2xx response, carrying the
 *  HTTP status so the caller can tell an auth failure (401/403 → session is
 *  dead, clear auth) apart from a transient failure (network/5xx → retryable). */
class PermissionFetchError extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`permissions request failed: ${status}`);
    this.name = 'PermissionFetchError';
    this.status = status;
  }
}

/** True when a permission-fetch failure means the session itself is invalid
 *  (401 Unauthorized / 403 Forbidden) rather than a transient/network fault. */
const isAuthFailure = (err: unknown): boolean =>
  err instanceof PermissionFetchError && (err.status === 401 || err.status === 403);

/** Fetch effective permission grants for a freshly-obtained session.
 *  Uses raw fetch because this runs before the http client's token is
 *  persisted. Throws on any non-2xx or network failure so the caller can
 *  surface a retryable error state instead of silently degrading to zero
 *  permissions — which would grey out every navigation with no feedback.
 *  A non-2xx throws `PermissionFetchError` so 401/403 can be routed to the
 *  auth-invalidation path instead of the retryable error state. */
async function fetchPermissionGrants(
  token: string,
): Promise<{ granted: string[]; role: AuthUser['role'] }> {
  const resp = await fetch(`${getBackendUrl()}/api/permissions`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  });
  if (!resp.ok) {
    throw new PermissionFetchError(resp.status);
  }
  // GET /api/permissions returns
  // `{ data: { permissions: [{key,module,action}], granted: string[], role } }`.
  // The caller's effective grants are `data.data.granted`; `data.data.role`
  // is the caller's tenant role.
  const data: unknown = await resp.json();
  const body = (data as { data?: { granted?: unknown; role?: unknown } } | null)?.data;
  const granted = body?.granted;
  return {
    granted: Array.isArray(granted) ? (granted as string[]) : [],
    role: toTenantRole(body?.role),
  };
}

export const BetterAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissionsError, setPermissionsError] = useState(false);

  // Identity of the currently-applied session, read synchronously (unlike the
  // `session` state, which is stale inside an in-flight async closure). Used by
  // `loadPermissions` to detect that the session was cleared/replaced while a
  // permission fetch was in flight, so a stale resolution can't resurrect a
  // logged-out user (or overwrite a newer session).
  const activeSessionTokenRef = useRef<string | null>(null);

  const applyAuthState = (nextUser: AuthUser | null, nextSession: AuthSession | null) => {
    activeSessionTokenRef.current = nextSession?.token ?? null;
    setUser(nextUser);
    setSession(nextSession);
    persistSession(nextSession);

    if (nextUser) {
      setUserId(nextUser.id);
      setUserProperties({
        user_role: nextUser.role,
        user_type: 'authenticated',
        preferred_language: i18n.language,
      });
    } else {
      setUserId('');
      // Clearing auth (logout / 401) also clears any stale permission-error banner.
      setPermissionsError(false);
    }
  };

  // Load permission grants and merge into user state. On failure, surface an
  // explicit retryable error state (see the provider render) rather than
  // silently degrading to zero permissions.
  const loadPermissions = async (nextUser: AuthUser, nextSession: AuthSession) => {
    // Pin the session we started against; if it changes mid-flight (logout /
    // re-login) we must not touch auth state on resolution.
    const startedToken = nextSession.token;
    try {
      const { granted, role } = await fetchPermissionGrants(startedToken);
      // Stale resolution: the session was cleared or replaced while we awaited.
      // Bailing here prevents resurrecting a logged-out user and re-persisting
      // its token (which would defeat the stale-token hardening).
      if (activeSessionTokenRef.current !== startedToken) return;
      applyAuthState({ ...nextUser, role, permissions: granted }, nextSession);
      setPermissionsError(false);
    } catch (err) {
      if (import.meta.env.DEV) console.error('[BetterAuthContext] fetchPermissions failed:', err);
      // Same staleness guard on the failure path: don't pin an error screen
      // onto a session that's already gone (Retry would be a no-op).
      if (activeSessionTokenRef.current !== startedToken) return;
      // A 401/403 means the session itself is dead — the raw fetch never
      // dispatches AUTH_UNAUTHORIZED_EVENT, so route it to the same auth-clear
      // path here. RouteGuard then redirects to /login. This is NOT a retryable
      // permissions error, so don't trap the user behind a Retry that can't win.
      if (isAuthFailure(err)) {
        applyAuthState(null, null);
        return;
      }
      setPermissionsError(true);
    }
  };

  const retryPermissions = async () => {
    if (user && session?.token) {
      await loadPermissions(user, session);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        setLoading(true);
        const result = await authClient.getSession();

        if (result.data) {
          if (import.meta.env.DEV) {
            console.log('[BetterAuthContext] initAuth - processing user data');
          }
          const nextUser = toAuthUser(result.data.user);
          const nextSession = toAuthSession(result.data as SessionPayload);
          applyAuthState(nextUser, nextSession);
          if (nextSession?.token && nextUser) {
            await loadPermissions(nextUser, nextSession);
          }
        } else {
          // No valid server session. Any residual token in localStorage is
          // stale (the server rejected/expired it) — clear it so it stops
          // riding on the Authorization header until a future 401. Do NOT
          // resurrect it into session state.
          applyAuthState(null, null);
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        applyAuthState(null, null);
      } finally {
        setLoading(false);
      }
    };

    void initAuth();
  }, []);

  // 401 from any HTTP call → clear local auth state. RouteGuard then
  // redirects to /login on the next render (SPA navigation, preserves
  // `location` so tests / deep-links survive).
  //
  // `useLayoutEffect` runs synchronously before paint and BEFORE any
  // sibling `useEffect` (including `initAuth` above) gets a chance to
  // fire its first await. That matters: `initAuth` calls
  // `authClient.getSession()` which may itself yield a 401 — the event
  // would otherwise be dispatched into a void because the listener
  // hadn't mounted yet. The earlier `useEffect`-based attach lost the
  // event in that race window.
  useLayoutEffect(() => {
    const onUnauthorized = () => {
      // Purge every cached query so a forced logout never leaves the
      // previous session's lists / details readable to the next user
      // on a shared device (#1155).
      queryClient.clear();
      applyAuthState(null, null);
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const signIn = async (credentials: { email: string; password: string }) => {
    try {
      const result = await authClient.signIn.email({
        email: credentials.email,
        password: credentials.password,
        rememberMe: true,
      });

      if (import.meta.env.DEV) {
        console.log('[BetterAuthContext] signIn result:', result);
      }

      if (result.error) {
        const msg = result.error.message || '';
        // Better Auth BASE_ERROR_CODES.EMAIL_NOT_VERIFIED = "Email not verified"
        // Returns HTTP 403 with this exact message string.
        const isEmailNotVerified = msg.toLowerCase().includes('email not verified');
        return {
          success: false,
          error: msg,
          emailNotVerified: isEmailNotVerified,
          status: result.error.status,
        };
      }

      if (result.data) {
        let nextUser = toAuthUser(result.data.user);
        let nextSession = toAuthSession(result.data as SessionPayload);

        // Better Auth's sign-in response `user` omits the admin plugin's
        // `banned` field (and the platform `role`) that get-session returns.
        // Re-query getSession and prefer its richer payload, falling back to
        // the sign-in payload on failure so a successful login never turns
        // into a failure. The tenant role is not read from here: it arrives
        // from GET /api/permissions in `loadPermissions` below (#2414).
        try {
          const sessionResult = await authClient.getSession();
          if (sessionResult.data) {
            nextUser = toAuthUser(sessionResult.data.user) ?? nextUser;
            nextSession = toAuthSession(sessionResult.data as SessionPayload) ?? nextSession;
          }
        } catch (err) {
          if (import.meta.env.DEV) {
            console.error('[BetterAuthContext] getSession after sign-in failed:', err);
          }
        }

        applyAuthState(nextUser, nextSession);

        if (nextSession?.token && nextUser) {
          await loadPermissions(nextUser, nextSession);
        }

        Analytics.login('email');
        return { success: true };
      }

      return { success: false, error: t('auth.loginFailed') };
    } catch (error) {
      console.error('Sign in error:', error);
      return { success: false, error: t('auth.unexpectedSignIn') };
    }
  };

  const signUp = async (userData: { email: string; password: string; name: string }) => {
    try {
      const result = await authClient.signUp.email({
        email: userData.email,
        password: userData.password,
        name: userData.name,
      });

      if (result.error) {
        return { success: false, error: result.error.message };
      }

      return result.data
        ? { success: true }
        : { success: false, error: t('auth.registrationFailed') };
    } catch (error) {
      console.error('Sign up error:', error);
      return { success: false, error: t('auth.unexpectedSignUp') };
    }
  };

  const signOut = async () => {
    try {
      Analytics.logout();
      await authClient.signOut();
    } finally {
      // Purge every cached query on logout — see the 401 handler above.
      queryClient.clear();
      applyAuthState(null, null);
    }
  };

  const refreshSession = async () => {
    try {
      const result = await authClient.getSession();

      if (result.data) {
        const payloadUser = toAuthUser(result.data.user);
        // Same user: take the identity fields from the payload and keep the
        // authorization fields (role, permissions) until `loadPermissions`
        // replaces them, so guards and nav do not drop out on refresh. A
        // different user starts fail-closed from `toAuthUser`.
        const nextUser =
          payloadUser && user?.id === payloadUser.id
            ? { ...payloadUser, role: user.role, permissions: user.permissions }
            : payloadUser;
        const nextSession = toAuthSession(result.data as SessionPayload);
        applyAuthState(nextUser, nextSession);
        if (nextSession?.token && nextUser) {
          await loadPermissions(nextUser, nextSession);
        }
      } else {
        applyAuthState(null, null);
      }
    } catch (error) {
      console.error('Refresh session error:', error);
      applyAuthState(null, null);
    }
  };

  const hasRole = (role: AuthUser['role']): boolean => {
    return user ? hasPermission(user.role, role) : false;
  };

  const contextValue: AuthContextType = {
    user,
    session,
    isAuthenticated: Boolean(user && session),
    loading,
    signIn,
    signUp,
    signOut,
    refreshSession,
    hasRole,
    isAdmin: isAdminUser(user),
    displayName: user ? user.name || user.email.split('@')[0] : t('user.unknown'),
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {loading ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="mt-2 text-muted-foreground">{t('auth.loadingAuth')}</p>
          </div>
        </div>
      ) : permissionsError ? (
        // Permission fetch failed. Block rendering behind an explicit,
        // retryable error rather than degrading to empty permissions (which
        // would grey out all navigation with no feedback).
        <div
          role="alert"
          data-testid="permissions-error"
          className="flex items-center justify-center min-h-screen px-4"
        >
          <div className="flex flex-col items-center text-center max-w-md">
            <p className="text-lg font-semibold">{t('auth.permissionsError.title')}</p>
            <p className="mt-2 text-muted-foreground">{t('auth.permissionsError.message')}</p>
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                data-testid="permissions-retry"
                onClick={() => void retryPermissions()}
                className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                {t('auth.permissionsError.retry')}
              </button>
              <button
                type="button"
                data-testid="permissions-signout"
                onClick={() => void signOut()}
                className="inline-flex items-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
              >
                {t('auth.permissionsError.signOut')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useBetterAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useBetterAuth must be used within a BetterAuthProvider');
  }
  return context;
};

// Stable identity so `usePermissions()` returns the same reference on
// every render where grants are absent — memoized consumers (bulk-action
// filters etc.) don't re-run on an incidental array churn.
const NO_PERMISSIONS: readonly string[] = Object.freeze([]);

/**
 * The caller's effective permission grants, or an empty list while they
 * are still loading / when read outside an `AuthProvider`.
 *
 * Unlike `useBetterAuth()` this does NOT throw without a provider: it is
 * a read-only convenience for in-page write-affordance gating (Create /
 * Edit / Delete / bulk actions in `ResourcePage`), where "no provider"
 * and "grants not yet fetched" both mean the same safe thing — show no
 * mutating affordance. Route-level enforcement still goes through
 * `PermissionGuard` / `useBetterAuth`; this never widens access.
 */
export const usePermissions = (): readonly string[] => {
  const context = useContext(AuthContext);
  return context?.user?.permissions ?? NO_PERMISSIONS;
};
